import React, { useCallback, useEffect, useRef, useState } from 'react';

import Alert from '.';
import type { AlertSize, IAlertShowParams } from '.';

interface AlertState {
  opened: boolean;
  size?: AlertSize;
  message?: React.ReactNode;
  title?: React.ReactNode;
  caption?: React.ReactNode;
  confirmation?: boolean;
  buttonOk?: React.ReactNode;
  buttonOkDanger?: boolean;
  buttonCancel?: React.ReactNode;
}

let lastPromise = Promise.resolve(false);
let globalAlert: (params: IAlertShowParams) => Promise<boolean>;

type AlertGlobalProviderType = React.FC & {
  show(params: IAlertShowParams): Promise<boolean>;
};

const AlertGlobalProvider: AlertGlobalProviderType = () => {
  const [state, setState] = useState<AlertState>({ opened: false });
  const promiseResolveRef = useRef<(result: boolean) => void>(null);

  const handleShow = useCallback((params: IAlertShowParams): Promise<boolean> => {
    const result = new Promise<boolean>(resolve => {
      promiseResolveRef.current = resolve;
      setState(prev => ({ ...prev, opened: true, confirmation: false, ...params }));
    });

    result.then(() => setState(prev => ({ ...prev, opened: false, buttonCancel: null, buttonOk: null })));
    return result;
  }, []);

  const handleClose = useCallback((ok: boolean) => {
    promiseResolveRef.current?.(ok);
  }, []);

  useEffect(() => {
    if (globalAlert) {
      throw new Error('Only one Alert.Global can be initialized');
    }

    globalAlert = handleShow;

    return () => {
      globalAlert = null;
    };
  }, [handleShow]);

  const { opened, message, title, caption, confirmation, buttonOk, buttonCancel, size, buttonOkDanger } = state;

  return (
    <Alert
      opened={opened}
      size={size}
      message={message}
      title={title}
      caption={caption}
      confirmation={confirmation}
      onClose={handleClose}
      buttonCancel={buttonCancel}
      buttonOkDanger={buttonOkDanger}
      buttonOk={buttonOk}
    />
  );
};

AlertGlobalProvider.show = async (params: IAlertShowParams): Promise<boolean> => {
  if (!globalAlert) {
    throw new Error('Please, initialize an Alert.Global before');
  }

  return (lastPromise = lastPromise.then(async () => {
    await new Promise(resolve => setTimeout(resolve, 300));
    return globalAlert(params);
  }));
};

export { AlertGlobalProvider };
